import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@mantine/core';
import { IconChevronRight, IconGenderFemale, IconGenderMale, IconMoodKid, IconUser, IconUsersGroup } from '@tabler/icons-react';
import { RIDES_SEX } from '@actschurch/ccms-sdk';
import { riderMissingStep, partySize } from '@/store/ridesWizard';
import { useCopy } from '@/lib/settings';
import { StepHeader } from '@/components/ui/StepHeader';
import { StickyActionBar } from '@/components/ui/StickyActionBar';
import { PickerCard, PickerGroup } from '@/components/ui/PickerCard';
import { Callout } from '@/components/ui/Callout';
import { LoadingState } from '@/components/ui/LoadingState';
import { PartyCounter } from '@/components/rides/PartyCompositionPicker';
import { sexFromGender, useRiderFlow } from './_shared';

type Part = 'who' | 'sex' | 'group' | 'child-who' | 'child-sex' | 'child-group';
const PARTS: Part[] = ['who', 'sex', 'group', 'child-who', 'child-sex', 'child-group'];

/**
 * Find a ride, who's travelling, one question per screen:
 *   who        Just me / Me and others / My child (under 18, travelling without me)
 *   sex        only when the profile doesn't say (solo)
 *   group      men and women, including me
 *   child-*    alone or with others (the group rule when settings.minorRequiresGroup), then boy or girl / the counts
 * The answer drives the safety matching (female-driver rule, child-safety clearance) on the next step.
 */
export default function RidePartyPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t, settings } = useCopy();
  const f = useRiderFlow();
  const { rider, setRider, profile, member, memberIsMinor } = f;
  const [tried, setTried] = useState(false);
  const q = params.get('q') as Part | null;
  const part: Part = q && PARTS.includes(q) ? q : 'who';
  const minorAge = settings.minorAgeYears;
  const knownSex = profile.data?.sexID || sexFromGender(member.data?.gender);

  const missing = riderMissingStep(rider, 'party');
  if (missing) return <Navigate to={missing} replace />;
  if (part.startsWith('child') && memberIsMinor) return <Navigate to="/ride/party" replace />;

  const go = (to: string) => { setTried(false); navigate(to); };
  const back = f.needsService ? '/ride/route?q=service' : '/ride/route?q=to';
  const size = partySize(rider);
  const groupRule = settings.minorRequiresGroup;

  const chooseSolo = () => {
    if (knownSex) {
      setRider({ party: 'solo', forMinor: false, soloSexId: knownSex, maleCount: knownSex === RIDES_SEX.Male ? 1 : 0, femaleCount: knownSex === RIDES_SEX.Female ? 1 : 0, chosenAvailabilityId: null });
      go('/ride/matches');
    } else {
      setRider({ party: 'solo', forMinor: false, chosenAvailabilityId: null });
      go('/ride/party?q=sex');
    }
  };
  const chooseGroup = (forMinor: boolean) => {
    const fresh = rider.party !== 'group' || rider.forMinor !== forMinor;
    setRider({ party: 'group', forMinor, soloSexId: null, maleCount: fresh ? 1 : rider.maleCount, femaleCount: fresh ? 1 : rider.femaleCount, chosenAvailabilityId: null });
    go(forMinor ? '/ride/party?q=child-group' : '/ride/party?q=group');
  };
  const setSex = (sexId: number, forMinor: boolean) => {
    setRider({ party: 'solo', forMinor, soloSexId: sexId, maleCount: sexId === RIDES_SEX.Male ? 1 : 0, femaleCount: sexId === RIDES_SEX.Female ? 1 : 0, chosenAvailabilityId: null });
    go('/ride/matches');
  };

  const lede = {
    who: t('ride.who_intro'),
    sex: t('ride.sex_intro'),
    group: t('ride.group_intro'),
    'child-who': t('ride.child_who_intro'),
    'child-sex': t('ride.sex_intro'),
    'child-group': t('ride.child_group_intro'),
  }[part];
  const title = {
    who: "Who's travelling?",
    sex: 'Are you male or female?',
    group: 'How many people are travelling?',
    'child-who': 'Is your child travelling alone?',
    'child-sex': 'Is your child a boy or a girl?',
    'child-group': 'How many people are travelling?',
  }[part];
  const backTo = part === 'who' ? back : part === 'child-sex' || part === 'child-group' ? '/ride/party?q=child-who' : '/ride/party';

  // Sticky Continue: only when the screen already has an answer.
  let next: string | null = null;
  if (part === 'who' && rider.party && (memberIsMinor || rider.forMinor !== null)) {
    next = rider.forMinor && !memberIsMinor ? '/ride/party?q=child-who' : rider.party === 'group' ? '/ride/party?q=group' : rider.soloSexId ? '/ride/matches' : '/ride/party?q=sex';
  }
  if ((part === 'group' || part === 'child-group') && rider.party === 'group') next = '/ride/matches';
  if (part === 'child-who' && rider.forMinor && rider.party) next = rider.party === 'group' ? '/ride/party?q=child-group' : '/ride/party?q=child-sex';
  const groupTooSmall = (part === 'group' || part === 'child-group') && size < 2;
  const onContinue = () => {
    if (groupTooSmall) { setTried(true); return; }
    if (next) go(next);
  };

  const loading = profile.isLoading || member.isLoading;

  return (
    <div className="rides-main rides-flow">
      <StepHeader step={f.stepOf('who')} total={f.total} title={title} lede={lede} backTo={backTo} chips={f.chips('who')} />
      <div className="rides-stepbody" key={part}>

      {part === 'who' && (loading ? <LoadingState label="Loading" count={2} /> : (
        <div className="rides-flow-stack">
          {memberIsMinor && <Callout tone="info">{t('ride.minor_member_note', undefined, { minorAge })}</Callout>}
          <PickerGroup ariaLabel="Who's travelling">
            <PickerCard
              selected={rider.party === 'solo' && !(rider.forMinor && !memberIsMinor)}
              onSelect={chooseSolo}
              media={<span className="rides-icontile" aria-hidden="true"><IconUser size={24} /></span>}
              hideIndicator
              trailing={<span className="rides-chev" aria-hidden="true"><IconChevronRight size={22} /></span>}
              title="Just me"
              description="I'm travelling on my own"
            />
            <PickerCard
              selected={rider.party === 'group' && !(rider.forMinor && !memberIsMinor)}
              onSelect={() => chooseGroup(memberIsMinor)}
              media={<span className="rides-icontile" aria-hidden="true"><IconUsersGroup size={24} /></span>}
              hideIndicator
              trailing={<span className="rides-chev" aria-hidden="true"><IconChevronRight size={22} /></span>}
              title="Me and others"
              description="Family or friends travelling with me"
            />
            {!memberIsMinor && (
              <PickerCard
                selected={rider.forMinor === true}
                onSelect={() => { setRider({ forMinor: true, party: rider.forMinor ? rider.party : null, chosenAvailabilityId: null }); go('/ride/party?q=child-who'); }}
                media={<span className="rides-icontile warm" aria-hidden="true"><IconMoodKid size={24} /></span>}
                hideIndicator
                trailing={<span className="rides-chev" aria-hidden="true"><IconChevronRight size={22} /></span>}
                title="My child"
                description={`A child under ${minorAge} travelling without me`}
              />
            )}
          </PickerGroup>
          {settings.femaleDriverRuleEnabled && <p className="rides-help"><b>Why we ask.</b> {t('matches.female_driver_note')}</p>}
        </div>
      ))}

      {(part === 'sex' || part === 'child-sex') && (
        <PickerGroup ariaLabel={title}>
          <PickerCard selected={rider.party === 'solo' && rider.soloSexId === RIDES_SEX.Female} onSelect={() => setSex(RIDES_SEX.Female, part === 'child-sex')} title={part === 'child-sex' ? 'A girl' : 'Female'} media={<span className="rides-icontile" aria-hidden="true"><IconGenderFemale size={24} /></span>} hideIndicator trailing={<span className="rides-chev" aria-hidden="true"><IconChevronRight size={22} /></span>} />
          <PickerCard selected={rider.party === 'solo' && rider.soloSexId === RIDES_SEX.Male} onSelect={() => setSex(RIDES_SEX.Male, part === 'child-sex')} title={part === 'child-sex' ? 'A boy' : 'Male'} media={<span className="rides-icontile" aria-hidden="true"><IconGenderMale size={24} /></span>} hideIndicator trailing={<span className="rides-chev" aria-hidden="true"><IconChevronRight size={22} /></span>} />
        </PickerGroup>
      )}

      {(part === 'group' || part === 'child-group') && (
        <div className="rides-flow-stack">
          <PartyCounter
            maleCount={rider.maleCount}
            femaleCount={rider.femaleCount}
            maxSeats={settings.maxPartySize}
            child={part === 'child-group'}
            onChange={(patch) => { setTried(false); setRider({ party: 'group', ...patch, chosenAvailabilityId: null }); }}
          />
          {tried && groupTooSmall && (
            <Callout tone="bad" live>A group is two or more people. If one person is travelling, go back and choose {part === 'child-group' ? '“Just my child”' : '“Just me”'}.</Callout>
          )}
        </div>
      )}

      {part === 'child-who' && (
        <div className="rides-flow-stack">
          {groupRule && <Callout tone="info">{t('booking.minor_group_rule')}</Callout>}
          <PickerGroup ariaLabel={title}>
            <PickerCard
              selected={rider.forMinor === true && rider.party === 'solo'}
              onSelect={() => { setRider({ party: 'solo', forMinor: true, chosenAvailabilityId: null }); go('/ride/party?q=child-sex'); }}
              title="Just my child"
              description="One child travelling on their own"
              disabled={groupRule}
              disabledNote="Not possible. Please see the rule above."
            />
            <PickerCard
              selected={rider.forMinor === true && rider.party === 'group'}
              onSelect={() => chooseGroup(true)}
              title="My child and others"
              description="For example, a brother, sister or friend"
            />
          </PickerGroup>
        </div>
      )}

      </div>
      {next && (
        <StickyActionBar>
          <Button fullWidth onClick={onContinue}>{next === '/ride/matches' ? 'See drivers' : 'Continue'}</Button>
        </StickyActionBar>
      )}
    </div>
  );
}
